import React from 'react';

import { useQuery } from '@tanstack/react-query';
import { ErrorAlert } from 'components/ErrorAlert';
import { FullScreenLoader } from 'components/FullScreenLoader';
import { useStatelessSessionStorage } from 'hooks/use-stateless-storage';
import { SmartAssistantForbiddenDomainAlert } from 'pages/GuestSmartAssistant/components/SmartAssistantForbiddenDomainAlert';
import { SmartAssistantForbiddenEmbeddingAlert } from 'pages/GuestSmartAssistant/components/SmartAssistantForbiddenEmbeddingAlert';
import { GuestSmartAssistantService } from 'services/guest-smart-assistant-service';
import { GUEST_SMART_ASSISTANT_QUERY_KEY } from 'shared/constants/query-keys';
import { getSessionConversationIdKey } from 'shared/constants/smart-assistant-chat';
import { GuestSmartAssistantData } from 'shared/interfaces/SmartAssistant';
import {
  updateBrowserTabMetadata,
  validateAllowedDomain,
  validateSmartAssistantEmbedding
} from 'shared/utils/guest-smart-assistant';

interface GuestSmartAssistantDataValidationGuardProps {
  smartAssistantId: string;
  children: (guestSmartAssistantData: GuestSmartAssistantData, avatarUrl?: string) => React.ReactNode;
}

export function GuestSmartAssistantDataValidationGuard({
  smartAssistantId,
  children
}: GuestSmartAssistantDataValidationGuardProps) {
  const { getItem: getSessionConversationId } = useStatelessSessionStorage({
    key: getSessionConversationIdKey(smartAssistantId)
  });
  const sessionConversationId = getSessionConversationId();

  const guestSmartAssistantDataQuery = useQuery({
    queryKey: [GUEST_SMART_ASSISTANT_QUERY_KEY, { smartAssistantId }],
    queryFn: () => GuestSmartAssistantService.getGuestSmartAssistantData(smartAssistantId, sessionConversationId),
    onSuccess: async (guestSmartAssistantData: GuestSmartAssistantData) => {
      await updateBrowserTabMetadata(guestSmartAssistantData.smartAssistant);
    },
    retry: false,
    refetchOnWindowFocus: false
  });

  if (guestSmartAssistantDataQuery.isLoading) return <FullScreenLoader />;
  // We don't need to handle the error type (not found, forbidden, etc.) here because the accessibility guard will handle it
  if (!guestSmartAssistantDataQuery.data || guestSmartAssistantDataQuery.isError)
    return <ErrorAlert w="100svw" h="100svh" />;
  if (!validateAllowedDomain(guestSmartAssistantDataQuery.data.smartAssistant))
    return <SmartAssistantForbiddenDomainAlert />;
  if (!validateSmartAssistantEmbedding(guestSmartAssistantDataQuery.data.smartAssistant))
    return <SmartAssistantForbiddenEmbeddingAlert />;

  return (
    <>{children(guestSmartAssistantDataQuery.data, guestSmartAssistantDataQuery.data?.smartAssistant.avatarUrl)}</>
  );
}
